import { CommonModule } from '@angular/common';
import { Component, Input, input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { Charity, SplitScreen, SplitScreenSectionsTypes } from '@domains';
import { FxFlexDirective, FxLayoutAlignDirective, FxLayoutDirective, InformationDialogComponent } from '@rspl-ui';
import { StarRatingModule } from 'angular-star-rating';

import { FxLayoutGapDirective } from '../../flex-layout/fx-layout-gap.directive';
import { ResolveUrlPipe } from '../../resolve-url/resolve-url.pipe';
import { Responsive, ResponsiveService } from '../../responsive';
import { CharitySponsoredSectionComponent } from './sections/charity-sponsored/charity-sponsored.component';
import { CourierSectionComponent } from './sections/courier/courier.component';
import { CoverageMapSectionComponent } from './sections/coverage-map/coverage-map.component';
import { CustomerServiceSectionComponent } from './sections/customer-service/customer-service.component';
import { ExtraMileSectionComponent } from './sections/extra-mile/extra-mile.component';
import { FaqSectionComponent } from './sections/faq/faq.component';
import { FreePickupsComponent } from './sections/free-pickups/free-pickups.component';
import { PartnersSectionComponent } from './sections/partners/partners.component';
import { ReviewsSectionComponent } from './sections/reviews/reviews.component';
import { SplitCardSectionComponent } from './sections/split-card/split-card.component';
import { UberSectionComponent } from './sections/uber/uber.component';
import { VeteransBehindResupplySectionComponent } from './sections/veterans-behind-resupply/veterans-behind-resupply.component';
import { WhyChooseResupplySectionComponent } from './sections/why-choose-resupply/why-choose-resupply.component';

@Component({
  selector: 'rspl-split-screen-v4',
  templateUrl: './split-screen-v4.component.html',
  styleUrl: './split-screen-v4.component.scss',
  imports: [
    FxLayoutDirective,
    FxLayoutAlignDirective,
    FxLayoutGapDirective,
    FxFlexDirective,
    MatIcon,
    StarRatingModule,
    CommonModule,
    ResolveUrlPipe,
  ],
})
export class SplitScreenV4Component extends Responsive implements OnInit {
  @Input() charity: Charity;
  @Input() config: SplitScreen;
  @Input() goToPrimary: (section: string) => void;
  @Input() showVideo: (section: string) => void;
  @Input() goToFree: () => void;
  openZendesk = input.required<() => void>();
  sectionTypes = SplitScreenSectionsTypes;

  preview = false;

  get configToUse() {
    if (this.preview) {
      return this.config;
    }
    return this.charity.meta;
  }

  get isDescriptionShorthend() {
    return this.isSmallWindowSize && this.configToUse.textPrimary.length > 500;
  }

  get shortText() {
    return (
      this.isDescriptionShorthend ? this.configToUse.textPrimary.substring(0, 500) + '...' : this.configToUse.textPrimary || ''
    ).replaceAll('\n', '</br>');
  }

  constructor(
    private dialog: MatDialog,
    public override responsiveService: ResponsiveService,
  ) {
    super(responsiveService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.preview = new URLSearchParams(window.location.search).get('isPreview') === 'true';
  }

  showText() {
    this.dialog.open(InformationDialogComponent, {
      width: '90vw',
      height: '80vh',
      data: {
        title: this.configToUse.title,
        message: this.configToUse.textPrimary?.replaceAll('\n', '</br>'),
        button: 'I understand',
      },
    });
  }

  goToPrimaryAction(section: string) {
    return this.preview ? () => {} : this.goToPrimary(section);
  }

  goToFreeAction() {
    return this.preview ? () => {} : this.goToFree();
  }

  showVideoAction(section: string) {
    return this.preview ? () => {} : this.showVideo(section);
  }

  sectionInputs(type: SplitScreenSectionsTypes) {
    const section = this.configToUse?.sections?.find((section) => section.type === type);

    return {
      goToPrimary: this.goToPrimaryAction.bind(this),
      goToFree: this.goToFreeAction.bind(this),
      sectionType: section.type,
      showVideo: this.showVideoAction.bind(this),
      options: {
        title: this.configToUse.title,
        text: this.configToUse.text,
        buttonText: this.configToUse.button,
        url: this.configToUse.url,
        logo: this.configToUse.logo,
        charityLogo: this.charity?.logo,
        hyperlinkUrl: this.configToUse.hyperlinkUrl,
        hyperlinkDisplayedText: this.configToUse.hyperlinkDisplayText,
        ...section.options,
      },
    };
  }

  sectionComponents(type: SplitScreenSectionsTypes) {
    switch (type) {
      case SplitScreenSectionsTypes.uber:
        return UberSectionComponent;
      case SplitScreenSectionsTypes.courier:
        return CourierSectionComponent;
      case SplitScreenSectionsTypes.freePickups:
        return FreePickupsComponent;
      case SplitScreenSectionsTypes.why:
        return WhyChooseResupplySectionComponent;
      case SplitScreenSectionsTypes.charity:
        return CharitySponsoredSectionComponent;
      case SplitScreenSectionsTypes.review:
        return ReviewsSectionComponent;
      case SplitScreenSectionsTypes.coverageMap:
        return CoverageMapSectionComponent;
      case SplitScreenSectionsTypes.veterans:
        return VeteransBehindResupplySectionComponent;
      case SplitScreenSectionsTypes.faq:
        return FaqSectionComponent;
      case SplitScreenSectionsTypes.partners:
        return PartnersSectionComponent;
      case SplitScreenSectionsTypes.extraMile:
        return ExtraMileSectionComponent;
      case SplitScreenSectionsTypes.customerService:
        return CustomerServiceSectionComponent;
      case SplitScreenSectionsTypes.splitCard:
        return SplitCardSectionComponent;
    }
  }
}
